import * as Yup from "yup";
import { format } from "date-fns";
import api from "../config/axiosConfig";
import { toast } from "react-hot-toast";
import { useParams } from "react-router-dom";
import { convertToFormData } from "../config/convertToFormData";
import { useEffect, useState, useMemo, useCallback } from "react";
import { useRfi } from "../contexts/RfiContext";
import { useSelector } from "react-redux";
import { hasPermission } from "../utils/utilityFunctions";
import { actionType } from "../utils/constant";

/**
 * Paint coat details form management hook with validation, API integration, and submission handling.
 * @param {string} coatType - Coating type ('primer', 'second', 'topcoat')
 * @param {string} stageId - Stage ID for submission
 * @param {boolean} isAPICallAllowed - Whether API calls are allowed
 */

export const usePaintCoatDetails = ({
  coatType,
  stageId,
  stage_number,
  isAPICallAllowed,
}) => {
  const { rfinumber } = useParams();
  const { profile } = useSelector((state) => state.profile);
  const {
    submitStageAPI,
    getRFIStages,
    skipStage,
    setPrimerData,
    setSecondCoatData,
    setTopCoatData,
  } = useRfi();
  const [paintCoatDetails, setPaintCoatDetails] = useState({});
  const [stageLoading, setStageLoading] = useState(true);
  const [skipStageLoading, setSkipStageLoading] = useState(false);

  // Helper function to get Context setter based on coat type
  const getContextSetter = useCallback(
    (type) => {
      switch (type) {
        case "primer":
          return setPrimerData;
        case "second":
          return setSecondCoatData;
        case "top":
        case "topcoat":
          return setTopCoatData;
        default:
          return null;
      }
    },
    [setPrimerData, setSecondCoatData, setTopCoatData]
  );

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        weatherCondition: Yup.string()
          .oneOf(
            ["clear", "rain", "cloudy"],
            "Please select a valid weather condition"
          )
          .required("Weather condition selection is required"),
        ambientCondition: Yup.object().shape({
          time: Yup.string(),
          dryBulb: Yup.number()
            .required("Dry bulb temperature is required")
            .typeError("Must be a valid number"),
          wetBulb: Yup.number()
            .required("Wet bulb temperature is required")
            .typeError("Must be a valid number"),
          humidity: Yup.number()
            .required("Humidity percentage is required")
            .min(0, "Humidity cannot be less than 0%")
            .max(100, "Humidity cannot exceed 100%")
            .typeError("Must be a valid number"),
          dewPoint: Yup.number()
            .required("Dew point temperature is required")
            .typeError("Must be a valid number"),
          substrateTemp: Yup.number()
            .required("Substrate temperature is required")
            .typeError("Must be a valid number"),
          substrateDewPoint: Yup.number()
            .required("Substrate temperature minus dew point is required")
            .typeError("Must be a valid number"),
          status: Yup.string(),
          finalInspection: Yup.string().required(
            "Final inspection result is required"
          ),
        }),

        coatingIdentification: Yup.object().shape({
          timeApplication: Yup.string().required(
            "Application time is required"
          ),
          painterId: Yup.string().required(
            "Painter identification is required"
          ),
          methodApplication: Yup.string().required(
            "Application method is required"
          ),

          brandNameColour: Yup.string().required(
            "Brand name, colour, and RAL code are required"
          ),
          mixingRatio: Yup.string().required("Paint mixing ratio is required"),
          partABatchNo: Yup.string().required(
            "Part A batch number is required"
          ),
          partADOM: Yup.string().required(
            "Part A date of manufacture is required"
          ),
          partBBatchNo: Yup.string().required(
            "Part B batch number is required"
          ),
          partBDOM: Yup.string().required(
            "Part B date of manufacture is required"
          ),
          thinnerBatchNo: Yup.string().required(
            "Thinner batch number is required"
          ),
          thinnerDOM: Yup.string().required(
            "Thinner date of manufacture is required"
          ),
          surfaceCondition: Yup.string().required(
            "Surface condition assessment is required"
          ),
          stripeCoat: Yup.string().required(
            "Stripe coat information is required"
          ),
          wetFilmThickness: Yup.number()
            .typeError("Wet film thickness must be a number")
            .required("Wet film thickness measurement is required"),
          dryFilmThickness: Yup.number()
            .typeError("Dry film thickness must be a number")
            .required("Dry film thickness measurement is required"),
          adhesionTest: Yup.string().required(
            "Adhesion test result is required"
          ),
          fullCure: Yup.boolean().oneOf(
            [true],
            "Full cure confirmation is required"
          ),
          dry: Yup.boolean().oneOf([true], "Dry confirmation is required"),

          // Documentation and Compliance
          drawingForSpecificRequirement: Yup.string().required(
            "Drawing reference for specific requirements is required"
          ),
          drawingPictures: Yup.array()
            .min(1, "At least one drawing picture is required")
            .max(5, "Maximum 5 drawing pictures are allowed")
            .required("Drawing pictures are required"),
          dftSpotTakenEvery: Yup.string().required(
            "DFT measurement frequency is required"
          ),
          remarks: Yup.string().required("Inspection remarks are required"),
        }),

        paintingChecklist: Yup.object().shape({
          parts: Yup.array().of(
            Yup.object().shape({
              painting_part_items: Yup.array().of(
                Yup.object().shape({
                  item_name: Yup.string().required(
                    "Inspection item name is required"
                  ),
                  visual_check: Yup.boolean().oneOf(
                    [true],
                    "Visual inspection is required"
                  ),
                  remark: Yup.string()
                    .trim()
                    .required("Inspection remark is required"),
                })
              ),
            })
          ),
        }),
      }),
    []
  );

  // Fetch paint coat details for the given rfiId on first render
  useEffect(() => {
    let active = true;
    const fetchPaintCoatDetails = async () => {
      if (!rfinumber || !isAPICallAllowed) return;
      if (
        !hasPermission(profile?.permissions?.actions, actionType.stage_view)
      ) {
        return;
      }
      try {
        setStageLoading?.(true);
        const res = await api.get(
          `/api/painting-checklist/${rfinumber}?coat_type=${encodeURIComponent(
            coatType || ""
          )}`
        );
        if (!active) return;
        const data = res?.data?.data || {};
        setPaintCoatDetails(data);
        const contextSetter = getContextSetter(coatType);
        if (contextSetter) {
          contextSetter(data);
        }
      } catch (e) {
        if (!active) return;
        toast.error(
          `Failed to load ${coatType} paint coat details. Please try again.`
        );
      } finally {
        setStageLoading?.(false);
      }
    };
    fetchPaintCoatDetails();
    return () => {
      active = false;
    };
  }, [
    rfinumber,
    coatType,
    isAPICallAllowed,
    profile?.permissions?.actions,
    getContextSetter,
  ]);

  // Derive initial values from paintCoatDetails if available
  const initialValues = useMemo(() => {
    const selectedCoating = paintCoatDetails?.coatings?.[0];
    const paintingChecklistParts = paintCoatDetails?.painting_checklist_parts;
    const coatingImages = paintCoatDetails?.coating_images;
    const paintingChecklistImages = paintCoatDetails?.painting_checklist_images;

    // Normalize parts to ensure correct defaults for form controls
    const normalizedParts = Array.isArray(paintingChecklistParts)
      ? paintingChecklistParts.map((part) => ({
          ...part,
          painting_part_items: Array.isArray(part?.painting_part_items)
            ? part.painting_part_items.map((it) => ({
                ...it,
                // Default visual_check to false when null/undefined
                visual_check: Boolean(it?.visual_check),
                // Remark is a text field in UI; default to empty string
                remark: it?.remark ?? "",
              }))
            : [],
        }))
      : [];

    if (!selectedCoating) {
      return {
        weatherCondition: "",
        ambientCondition: {
          time: format(new Date(), "dd/MM/yyyy"),
          dryBulb: "",
          wetBulb: "",
          humidity: "",
          dewPoint: "",
          substrateTemp: "",
          substrateDewPoint: "",
          status: "Paint",
          finalInspection: "",
        },
        coatingIdentification: {
          timeApplication: "",
          painterId: "",
          methodApplication: "",
          brandNameColour: "",
          mixingRatio: "",
          partABatchNo: "",
          partADOM: "",
          partBBatchNo: "",
          partBDOM: "",
          thinnerBatchNo: "",
          thinnerDOM: "",
          surfaceCondition: "",
          stripeCoat: "",
          wetFilmThickness: "",
          dryFilmThickness: "",
          adhesionTest: "",
          fullCure: false,
          dry: false,
          drawingForSpecificRequirement: "",
          dftSpotTakenEvery: "",
          remarks: "",
          drawingPictures: [],
        },
        paintingChecklist: {
          parts: normalizedParts,
          images: Array.isArray(paintingChecklistImages)
            ? paintingChecklistImages.filter(Boolean).map((img) => ({ ...img }))
            : [],
        },
      };
    }

    // Determine weatherCondition from booleans
    let weatherCondition = "";
    if (selectedCoating.clear) weatherCondition = "clear";
    else if (selectedCoating.rain) weatherCondition = "rain";
    else if (selectedCoating.cloudy) weatherCondition = "cloudy";

    // Transform coating images
    const pictures = Array.isArray(coatingImages)
      ? coatingImages.filter(Boolean).map((img) => ({ ...img }))
      : [];
    const checklistPictures = Array.isArray(paintingChecklistImages)
      ? paintingChecklistImages.filter(Boolean).map((img) => ({ ...img }))
      : [];

    return {
      weatherCondition,
      ambientCondition: {
        time: selectedCoating.time
          ? format(new Date(selectedCoating.time), "dd/MM/yyyy")
          : "",
        dryBulb: selectedCoating.dry_bulb ?? "",
        wetBulb: selectedCoating.wet_bulb ?? "",
        humidity: selectedCoating.humidity ?? "",
        dewPoint: selectedCoating.dew_point ?? "",
        substrateTemp: selectedCoating.substrate_temp ?? "",
        substrateDewPoint: selectedCoating.substrate_temp_minus_dew_point ?? "",
        status: selectedCoating.status || "Paint",
        finalInspection: selectedCoating.final_inspection || "",
      },
      coatingIdentification: {
        timeApplication: selectedCoating.time_application || "",
        painterId: selectedCoating.painter_id || "",
        methodApplication: selectedCoating.method_application || "",
        brandNameColour: selectedCoating.brand_name_colour_ral || "",
        mixingRatio: selectedCoating.mixing_ratio || "",
        partABatchNo: selectedCoating.part_a_batch_no || "",
        partADOM: selectedCoating.part_a_dom || "",
        partBBatchNo: selectedCoating.part_b_batch_no || "",
        partBDOM: selectedCoating.part_b_dom || "",
        thinnerBatchNo: selectedCoating.thinner_batch_no || "",
        thinnerDOM: selectedCoating.thinner_dom || "",
        surfaceCondition: selectedCoating.surface_condition || "",
        stripeCoat: selectedCoating.stripe_coat || "",
        wetFilmThickness: selectedCoating.wet_film_thickness ?? "",
        dryFilmThickness: selectedCoating.dry_film_thickness_total ?? "",
        adhesionTest: selectedCoating.adhesion_test || "",
        fullCure: Boolean(selectedCoating.full_cure),
        dry: Boolean(selectedCoating.dry),
        drawingForSpecificRequirement:
          selectedCoating.drawing_for_specific_requirement || "",
        dftSpotTakenEvery: selectedCoating.dft_spot_taken_every || "",
        remarks: selectedCoating.remarks || "",
        drawingPictures: pictures,
      },
      paintingChecklist: { parts: normalizedParts, images: checklistPictures },
    };
  }, [paintCoatDetails]);

  // Handle form submission to API
  const handleFormSubmit = async (values, { setSubmitting, setErrors }) => {
    if (!hasPermission(profile?.permissions?.actions, actionType.stage_edit)) {
      return;
    }
    try {
      // Validate required fields
      if (!rfinumber) {
        throw new Error("Missing rfi_id for painting checklist submission");
      }

      const coating_id = paintCoatDetails?.coatings?.[0]?.coating_id;
      const wc = values?.weatherCondition;
      const ac = values?.ambientCondition || {};
      const ci = values?.coatingIdentification || {};
      const parts = values?.paintingChecklist?.parts || [];
      const paintingImages = values?.paintingChecklist?.images || [];

      const payload = {
        rfi_id: rfinumber,
        coating_id,
        coat_type: coatType,
        // Send only the painting_part_items with required identifiers and values
        painting_checklist_parts: parts.map((each) => {
          const {
            rfi_id,
            coat_type,
            painting_checklist_template_id,
            created_by,
            ...rest
          } = each;
          return {
            ...rest,
            painting_part_items: rest.painting_part_items?.map((item) => {
              const { painting_checklist_part_id, created_by, ...itemRest } =
                item;
              return itemRest;
            }),
          };
        }),
        // Painting checklist images - handle both new files and existing images
        painting_checklist_images: paintingImages.map((each) => {
          // New upload: extract the File object
          if (each.file instanceof File) return each.file;
          return each;
        }),
        clear: wc === "clear",
        rain: wc === "rain",
        cloudy: wc === "cloudy",
        dry_bulb: ac.dryBulb ? Number(ac.dryBulb) : null,
        wet_bulb: ac.wetBulb ? Number(ac.wetBulb) : null,
        humidity: ac.humidity ? Number(ac.humidity) : null,
        dew_point: ac.dewPoint ? Number(ac.dewPoint) : null,
        substrate_temp: ac.substrateTemp ? Number(ac.substrateTemp) : null,
        substrate_temp_minus_dew_point: ac.substrateDewPoint
          ? Number(ac.substrateDewPoint)
          : null,
        status: ac.status || "",
        final_inspection: ac.finalInspection || "",
        painter_id: ci.painterId || "",
        method_application: ci.methodApplication || "",
        brand_name_colour_ral: ci.brandNameColour || "",
        mixing_ratio: ci.mixingRatio || "",

        // Batch tracking information
        part_a_batch_no: ci.partABatchNo || "",
        part_a_dom: ci.partADOM || "",
        part_b_batch_no: ci.partBBatchNo || "",
        part_b_dom: ci.partBDOM || "",
        thinner_batch_no: ci.thinnerBatchNo || "",
        thinner_dom: ci.thinnerDOM || "",

        // Surface and application details
        surface_condition: ci.surfaceCondition || "",
        stripe_coat: ci.stripeCoat || "",

        // Thickness measurements
        wet_film_thickness: ci.wetFilmThickness
          ? Number(ci.wetFilmThickness)
          : null,
        dry_film_thickness_total: ci.dryFilmThickness
          ? Number(ci.dryFilmThickness)
          : null,

        // Quality checks
        adhesion_test: ci.adhesionTest || "",
        full_cure: Boolean(ci.fullCure),
        dry: Boolean(ci.dry),

        // Documentation and compliance
        drawing_for_specific_requirement:
          ci.drawingForSpecificRequirement || "",
        dft_spot_taken_every: ci.dftSpotTakenEvery || "",
        remarks: ci.remarks || "",

        // File uploads - handle both new files and existing images
        images: ci.drawingPictures.map((each) => {
          if (each.file instanceof File) {
            return each.file;
          }
          return each;
        }),
      };
      // ==========================================
      // VALIDATION AND SUBMISSION
      // ==========================================
      // Convert to FormData for file upload support
      const formData = convertToFormData(payload);
      // Submit to API
      const res = await api.post("/api/painting-checklist", formData);
      const coatLabel = coatType.charAt(0).toUpperCase() + coatType.slice(1);
      if (stage_number !== "Stage_5") {
        await submitStageAPI({ stage_id: stageId });
      }
      toast.success(
        res?.data?.message || `${coatLabel} coat created successfully`
      );

      // Refetch and update Context after successful submission
      const updatedRes = await api.get(
        `/api/painting-checklist/${rfinumber}?coat_type=${encodeURIComponent(
          coatType || ""
        )}`
      );
      const updatedData = updatedRes?.data.data || {};
      setPaintCoatDetails(updatedData);
      const contextSetter = getContextSetter(coatType);
      if (contextSetter) {
        contextSetter(updatedData);
      }
      await getRFIStages(rfinumber);
    } catch (error) {
      console.error("Form submission error:", error);
      setErrors({ submit: "Failed to submit form. Please try again." });
      toast.error(
        error?.response?.data?.message || "Failed to submit painting checklist"
      );
    } finally {
      setSubmitting(false);
    }
  };

  // Build approval handler with validation-first submission
  const buildHandleSendForApproval = (
    validateForm,
    submitForm,
    setTouched,
    values
  ) => {
    return async () => {
      const parts = values?.paintingChecklist?.parts || [];
      const touchedParts = Array.isArray(parts)
        ? parts.map((p) => ({
            painting_part_items: Array.isArray(p?.painting_part_items)
              ? p.painting_part_items.map(() => ({
                  item_name: true,
                  visual_check: true,
                  remark: true,
                }))
              : [],
          }))
        : [];

      setTouched({
        weatherCondition: true,
        ambientCondition: {
          time: true,
          dryBulb: true,
          wetBulb: true,
          humidity: true,
          dewPoint: true,
          substrateTemp: true,
          substrateDewPoint: true,
          status: true,
          finalInspection: true,
        },
        coatingIdentification: {
          timeApplication: true,
          painterId: true,
          methodApplication: true,
          brandNameColour: true,
          mixingRatio: true,

          // Batch tracking
          partABatchNo: true,
          partADOM: true,
          partBBatchNo: true,
          partBDOM: true,
          thinnerBatchNo: true,
          thinnerDOM: true,

          // Surface and application conditions
          surfaceCondition: true,
          stripeCoat: true,

          // Measurements
          wetFilmThickness: true,
          dryFilmThickness: true,

          // Quality checks
          adhesionTest: true,
          fullCure: true,
          dry: true,

          // Documentation
          drawingForSpecificRequirement: true,
          drawingPictures: true,
          dftSpotTakenEvery: true,
          remarks: true,
        },

        // Dynamic painting checklist
        paintingChecklist: {
          parts: touchedParts,
        },
      });

      // ==========================================
      // VALIDATE AND SUBMIT OR FOCUS ERROR
      // ==========================================

      const errors = await validateForm();

      if (Object.keys(errors).length === 0) {
        // No validation errors - proceed with submission
        submitForm();
      } else {
        // Validation errors exist - scroll to and focus first error field
        setTimeout(() => {
          const firstErrorElement = document.querySelector(
            ".Mui-error input, .Mui-error textarea"
          );
          if (firstErrorElement) {
            firstErrorElement.scrollIntoView({
              behavior: "smooth",
              block: "center",
            });
            firstErrorElement.focus();
          }
        }, 100); // Small delay to ensure DOM updates are complete
      }
    };
  };

  const handleSkipStage = async () => {
    if (!hasPermission(profile?.permissions?.actions, actionType.stage_skip)) {
      toast.error("You do not have permission to skip this stage");
      return;
    }
    setSkipStageLoading(true);
    const response = await skipStage({
      stage_id: stageId,
      skip_stage: true,
    });
    if (response.success) {
      toast.success(response?.data?.message || "Stage skipped successfully");
    } else {
      toast.error(response?.error?.message || "Failed to skip stage");
    }
    setSkipStageLoading(false);
    await getRFIStages(rfinumber);
  };

  return {
    validationSchema,
    initialValues,
    stageLoading,
    skipStageLoading,
    handleFormSubmit,
    buildHandleSendForApproval,
    handleSkipStage,
    paintCoatDetails,
  };
};
